import { useEffect, useState } from "react";
import { Link, Navigate, NavLink, Route, Routes, useLocation } from "react-router-dom";
import { useDotBotsAuth } from "@dotbots-boutique/auth-sdk/react";
import TicketList from "./pages/TicketList";
import TicketDetail from "./pages/TicketDetail";
import ContactList from "./pages/ContactList";
import KnowledgeBase from "./pages/KnowledgeBase";
import Settings from "./pages/Settings";
import Admin from "./pages/Admin";
import AiReports from "./pages/AiReports";
import AutoArchiveRules from "./pages/AutoArchiveRules";
import AppManual from "./pages/AppManual";
import { hasPermission, roleLabels } from "./lib/permissions";
import { readTicketsUrl, saveRoute } from "./lib/routeMemory";
import { useT } from "./i18n/I18nProvider";
import { Avatar } from "./components/shared/Avatar";
import {
  AdminIcon,
  ContactsIcon,
  KnowledgeIcon,
  ReportIcon,
  SettingsIcon,
  TicketIcon,
} from "./components/shared/Icons";
import { ModalHost } from "./components/shared/modals";

// Persist the active route in localStorage on every navigation. The matching
// restore is performed synchronously before React mounts (see main.tsx), so
// HashRouter already has the right URL by the time we render.
function RouteMemory() {
  const location = useLocation();
  useEffect(() => {
    saveRoute(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

export default function App() {
  const { user } = useDotBotsAuth();
  const roles = user?.roles ?? [];
  const t = useT();
  // Admin and Settings are admin-only. Agents and viewers don't see the
  // nav links at all and get bounced from the routes themselves (see
  // Admin.tsx / Settings.tsx). agents.write and settings.write are both
  // unique to the admin role in ROLE_PERMISSIONS.
  const showAdmin = hasPermission(roles, "agents.write");
  const showSettings = hasPermission(roles, "settings.write");
  // Viewers can read tickets but not create them — hide the sidebar CTA.
  const canCreateTicket = hasPermission(roles, "tickets.write");
  // AI reports need both AI access and ticket read — hides the link
  // for viewers whose org hasn't granted AI.
  const showReports = hasPermission(roles, "ai.use") && hasPermission(roles, "tickets.read");

  // Read the persisted last-known tickets URL so the "Tickets" nav
  // link returns the operator to their previous filter/sort/view
  // state when they bounce off to /contacts and back. Recomputed on
  // every navigation so the latest /tickets visit (which writes via
  // saveTicketsUrl in TicketList) is reflected immediately.
  const location = useLocation();
  const [ticketsUrl, setTicketsUrl] = useState<string>(() => readTicketsUrl());
  useEffect(() => { setTicketsUrl(readTicketsUrl()); }, [location.pathname, location.search]);

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <span className="sidebar-brand-icon"><TicketIcon size={18} /></span>
          <div className="sidebar-brand-text">
            <div className="sidebar-brand-name">{t("appName")}</div>
            {user?.orgName && <div className="sidebar-brand-sub">{user.orgName.toUpperCase()}</div>}
          </div>
        </div>

        {canCreateTicket && (
          <Link className="sidebar-cta" to="/tickets?new=1">
            <span aria-hidden="true">+</span> {t("tickets.newButton")}
          </Link>
        )}

        <nav className="sidebar-nav">
          <NavLink to={ticketsUrl}><TicketIcon /><span>{t("nav.tickets")}</span></NavLink>
          <NavLink to="/contacts"><ContactsIcon /><span>{t("nav.contacts")}</span></NavLink>
          <NavLink to="/knowledge"><KnowledgeIcon /><span>{t("nav.knowledge")}</span></NavLink>
          {showReports && <NavLink to="/reports"><ReportIcon /><span>{t("nav.reports")}</span></NavLink>}
          {showAdmin && <NavLink to="/auto-archive"><AdminIcon /><span>{t("nav.autoArchive")}</span></NavLink>}
          {showAdmin && <NavLink to="/admin"><AdminIcon /><span>{t("nav.admin")}</span></NavLink>}
          {showSettings && <NavLink to="/settings"><SettingsIcon /><span>{t("nav.settings")}</span></NavLink>}
        </nav>

        {user && (
          <div className="sidebar-user-card">
            <Avatar name={user.name ?? user.email ?? "?"} size={36} />
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user.name ?? user.email}</div>
              <div className="sidebar-user-role">{roleLabels(roles)}</div>
            </div>
          </div>
        )}
      </aside>
      <main className="main">
        <RouteMemory />
        <Routes>
          {/* Tickets is the new landing route now that the dashboard is gone. */}
          <Route path="/" element={<Navigate to={ticketsUrl} replace />} />
          <Route path="/tickets" element={<TicketList />} />
          <Route path="/tickets/:id" element={<TicketDetail />} />
          <Route path="/contacts" element={<ContactList />} />
          <Route path="/knowledge" element={<KnowledgeBase />} />
          <Route path="/reports" element={<AiReports />} />
          <Route path="/auto-archive" element={<AutoArchiveRules />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/settings" element={<Settings />} />
          {/* User manual — marketplace links here from the app's
              Links section (DotBots guide 3.55.0). */}
          <Route path="/app-manual" element={<AppManual />} />
          <Route path="*" element={<div>{t("common.notFound")}</div>} />
        </Routes>
      </main>

      {/* Bottom tab bar — visible only on mobile via CSS @media query. */}
      <nav className="bottom-nav" aria-label="Primary">
        <NavLink to={ticketsUrl}>
          <TicketIcon />
          <span>{t("nav.tickets")}</span>
        </NavLink>
        <NavLink to="/contacts">
          <ContactsIcon />
          <span>{t("nav.contacts")}</span>
        </NavLink>
        <NavLink to="/knowledge">
          <KnowledgeIcon />
          <span>{t("nav.knowledge")}</span>
        </NavLink>
        {showAdmin && (
          <NavLink to="/admin">
            <AdminIcon />
            <span>{t("nav.admin")}</span>
          </NavLink>
        )}
        {showSettings && (
          <NavLink to="/settings">
            <SettingsIcon />
            <span>{t("nav.settings")}</span>
          </NavLink>
        )}
      </nav>
      <ModalHost />
    </div>
  );
}
